/**
 * product.js — product detail.
 *
 * Two structural changes:
 *   1. Media is a proper gallery: a stage with prev/next and zoom controls,
 *      plus a thumbnail rail, and click-through to the fullscreen lightbox.
 *   2. There is no price. Products are quoted individually, so the panel that
 *      would hold a price instead opens a quote request that lands in the
 *      Ticket table for the admins to answer by email.
 */

import {qs, qsa, esc, img, icon, apiPost, lockScroll, emptyState} from '../lib/dom.js';
import {gettext, num, localePath, messages} from '../lib/i18n.js';
import {open as openLightbox} from '../ui/lightbox.js';
import {enhanceForm, fieldMarkup, setFormBusy, showFormMessage} from '../ui/forms.js';

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;

let gallery = [];
let mediaIndex = 0;
let mediaZoom = 1;

/* ------------------------------------------------------------------ *
 * Media column
 * ------------------------------------------------------------------ */

function renderMedia(images, title) {
    if (!images.length) {
        return `<div class="nv-product-media">
      <div class="nv-product-media__stage">
        <span class="nv-card__placeholder">${icon('box-open')}</span>
      </div>
    </div>`;
    }

    const multiple = images.length > 1;

    return `
    <div class="nv-product-media">
      <div class="nv-product-media__stage" data-media-stage>
        ${img(images[0].image, title)}

        ${multiple ? `
          <button type="button" class="nv-product-media__nav nv-product-media__nav--prev"
                  data-media-prev aria-label="${gettext('Previous image')}">
            <i class="fa-solid fa-chevron-left nv-icon-flip" aria-hidden="true"></i>
          </button>
          <button type="button" class="nv-product-media__nav nv-product-media__nav--next"
                  data-media-next aria-label="${gettext('Next image')}">
            <i class="fa-solid fa-chevron-right nv-icon-flip" aria-hidden="true"></i>
          </button>` : ''}

        <div class="nv-product-media__tools">
          <button type="button" class="nv-product-media__tool" data-media-zoom-out
                  aria-label="${gettext('Zoom out')}" disabled>
            <i class="fa-solid fa-magnifying-glass-minus" aria-hidden="true"></i>
          </button>
          <button type="button" class="nv-product-media__tool" data-media-zoom-in
                  aria-label="${gettext('Zoom in')}">
            <i class="fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
          </button>
          <button type="button" class="nv-product-media__tool" data-media-full
                  aria-label="${gettext('View fullscreen')}">
            <i class="fa-solid fa-expand" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      ${multiple ? `
        <div class="nv-product-thumbs">
          ${images.map((image, i) => `
            <button type="button" class="nv-product-thumbs__item ${i === 0 ? 'is-active' : ''}"
                    data-media-thumb="${i}"
                    aria-label="${gettext('Image')} ${num(i + 1)}">
              ${img(image.image, image.caption || '')}
            </button>`).join('')}
        </div>` : ''}
    </div>`;
}

/* ------------------------------------------------------------------ *
 * Info column
 * ------------------------------------------------------------------ */

function renderSpecs(product) {
    const rows = [];

    const typeLabels = {
        product: gettext('Product'),
        service: gettext('Service'),
        course: gettext('Course'),
        book: gettext('Book'),
    };

    // Add a translation map for the delivery modes
    const courseModeLabels = {
        external: gettext('External'),
        internal: gettext('Internal (In-house)'),
    };

    if (product.product_type) {
        rows.push([gettext('Type'), typeLabels[product.product_type] || product.product_type]);
    }
    if (product.category) rows.push([gettext('Category'), product.category]);

    // Use the translation map here
    if (product.course_mode) {
        rows.push([gettext('Delivery'), courseModeLabels[product.course_mode] || product.course_mode]);
    }

    (product.extra_fields ? Object.entries(product.extra_fields) : [])
        .forEach(([key, value]) => rows.push([key, value]));

    if (!rows.length) return '';

    return `
    <div class="nv-spec-list">
      ${rows.map(([key, value]) => `
        <div class="nv-spec-list__row">
          <div class="nv-spec-list__key">${esc(key)}</div>
          <div class="nv-spec-list__val">${esc(num(value))}</div>
        </div>`).join('')}
    </div>`;
}

function renderQuotePanel(product) {
    return `
    <div class="nv-quote-panel">
      <span class="nv-quote-panel__label">
        ${icon('tag')} <span>${gettext('Pricing')}</span>
      </span>
      <p class="nv-quote-panel__headline">${gettext('Priced on request')}</p>
      <p class="nv-quote-panel__text">
        ${gettext('This item is priced according to your scope and requirements. Submit a request and our team will follow up by email.')}
      </p>
      <div class="nv-quote-panel__actions">
        <button type="button" class="nv-btn nv-btn--accent nv-btn--lg" data-open-quote
                data-product-id="${esc(product.id)}" data-product-title="${esc(product.title)}">
          ${icon('file-invoice')}
          <span>${gettext('Request a quote')}</span>
        </button>
      </div>
    </div>`;
}

/* ------------------------------------------------------------------ *
 * Quote modal
 * ------------------------------------------------------------------ */

function quoteModalMarkup(productId, productTitle) {
    return `
    <div class="nv-modal" id="quote-modal" role="dialog" aria-modal="true"
         aria-labelledby="quote-modal-title">
      <div class="nv-modal__panel">
        <div class="nv-modal__head">
          <div>
            <h2 class="nv-modal__title" id="quote-modal-title">
              ${gettext('Request a quote')}
            </h2>
            <p class="nv-modal__subtitle">${esc(productTitle)}</p>
          </div>
          <button type="button" class="nv-modal__close" data-close-quote
                  aria-label="${gettext('Close')}">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </div>

        <form class="nv-form nv-modal__body" id="quote-form" novalidate>
          <input type="hidden" name="product" value="${esc(productId)}">
          <input type="hidden" name="ticket_type" value="quote">

          <div class="nv-form__row">
            ${fieldMarkup({
        name: 'name', label: gettext('Full name'),
        required: true, placeholder: gettext('Your name'),
    })}
            ${fieldMarkup({
        name: 'email', label: gettext('Email'), type: 'email',
        placeholder: gettext('Optional'),
    })}
        </div>

    <div class="nv-form__row">
            ${fieldMarkup({
        name: 'phone', label: gettext('Phone'), type: 'tel',
        required: true, placeholder: num('09123456789'),
    })}
            ${fieldMarkup({
        name: 'subject', label: gettext('Subject'),
        required: true,
        value: `${gettext('Request a quote')} - ${productTitle}`,
        placeholder: gettext('Subject of your request'),
    })}
          </div>

          ${fieldMarkup({
        name: 'message', label: gettext('Message'), rows: 5, required: true,
        placeholder: gettext('Scope, number of participants, timeline, anything else…'),
    })}

          <div class="nv-modal__foot">
            <button type="button" class="nv-btn nv-btn--ghost" data-close-quote>
              <span>${gettext('Cancel')}</span>
            </button>
            <button type="submit" class="nv-btn nv-btn--accent">
              ${icon('paper-plane')}
              <span>${gettext('Send request')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>`;
}

function openQuoteModal(productId, productTitle) {
    document.getElementById('quote-modal')?.remove();

    const host = document.createElement('div');
    host.innerHTML = quoteModalMarkup(productId, productTitle);
    const modal = host.firstElementChild;
    document.body.appendChild(modal);

    requestAnimationFrame(() => modal.classList.add('is-open'));
    lockScroll(true);

    const close = () => {
        modal.classList.remove('is-open');
        lockScroll(false);
        setTimeout(() => modal.remove(), 200);
    };

    qsa('[data-close-quote]', modal).forEach((btn) => btn.addEventListener('click', close));
    modal.addEventListener('click', (event) => {
        if (event.target === modal) close();
    });
    document.addEventListener('keydown', function onEsc(event) {
        if (event.key === 'Escape') {
            close();
            document.removeEventListener('keydown', onEsc);
        }
    });

    const form = qs('#quote-form', modal);
    enhanceForm(form, {
        onSubmit: async (payload) => {
            setFormBusy(form, true);
            try {
                await apiPost('/api/tickets/', payload);
                form.innerHTML = `
          <div class="nv-alert nv-alert--success" role="status">
            <i class="fa-solid fa-circle-check nv-alert__icon" aria-hidden="true"></i>
            <div class="nv-alert__body">
              <p class="nv-alert__title">${gettext('Received')}</p>
              <p>${messages.submitSuccess()}</p>
            </div>
          </div>
          <div class="nv-modal__foot">
            <button type="button" class="nv-btn nv-btn--primary" data-close-quote>
              <span>${gettext('Close')}</span>
            </button>
          </div>`;
                qsa('[data-close-quote]', form).forEach((b) => b.addEventListener('click', close));
            } catch (err) {
                setFormBusy(form, false);
                showFormMessage(form, {
                    type: 'danger',
                    text: err.status === 0 ? messages.networkError() : (err.message || messages.generic()),
                });
            }
        },
    });

    qs('input[name="name"]', form)?.focus();
}

/* ------------------------------------------------------------------ *
 * Render + wire
 * ------------------------------------------------------------------ */

export function renderProductDetail(data) {
    const product = data.product || data;
    if (!product || !product.title) {
        return `<div class="nv-container nv-section">${emptyState({
            iconName: 'triangle-exclamation',
            title: gettext('Product not found'),
            action: `<a href="${localePath('/products/')}" class="nv-btn nv-btn--primary">
                 <span>${gettext('Back to products')}</span>
               </a>`,
        })}</div>`;
    }

    gallery = (product.images || []).filter((i) => i.image);
    mediaIndex = 0;
    mediaZoom = 1;

    return `
    <section class="nv-page-hero nv-page-hero--compact">
      <div class="nv-container nv-page-hero__inner">
        <nav class="nv-breadcrumb" aria-label="${gettext('Breadcrumb')}">
          <a href="${esc(localePath('/'))}">${gettext('Home')}</a>
          <i class="fa-solid fa-chevron-left nv-breadcrumb__sep nv-icon-flip" aria-hidden="true"></i>
          <a href="${esc(localePath('/products/'))}">${gettext('Products')}</a>
          <i class="fa-solid fa-chevron-left nv-breadcrumb__sep nv-icon-flip" aria-hidden="true"></i>
          <span>${esc(product.title)}</span>
        </nav>
        <h1 class="nv-page-hero__title">${esc(product.title)}</h1>
      </div>
    </section>

    <section class="nv-section">
      <div class="nv-container">
        <div class="nv-product">
          ${renderMedia(gallery, product.title)}

          <div class="nv-product-info">
            ${product.short_description
        ? `<p class="nv-product-info__lead">${esc(product.short_description)}</p>` : ''}
            ${renderQuotePanel(product)}
            ${renderSpecs(product)}
          </div>
        </div>

        ${product.description ? `
          <div class="nv-section nv-section--tight">
            <h2 class="nv-section-title">${gettext('Description')}</h2>
            <div class="nv-prose">${product.description}</div>
          </div>` : ''}

        <div class="nv-cluster" style="margin-block-start: var(--nv-space-8);">
          <a href="${esc(localePath('/products/'))}" class="nv-btn nv-btn--outline">
            <i class="fa-solid fa-arrow-left nv-icon-flip" aria-hidden="true"></i>
            <span>${gettext('Back to products')}</span>
          </a>
        </div>
      </div>
    </section>`;
}

export function initProductDetail(scope) {
    const stage = qs('[data-media-stage]', scope);
    const image = stage ? qs('img', stage) : null;

    const setZoom = (value) => {
        mediaZoom = Math.min(Math.max(value, ZOOM_MIN), ZOOM_MAX);
        if (image) image.style.transform = `scale(${mediaZoom})`;
        const out = qs('[data-media-zoom-out]', scope);
        const zin = qs('[data-media-zoom-in]', scope);
        if (out) out.disabled = mediaZoom <= ZOOM_MIN;
        if (zin) zin.disabled = mediaZoom >= ZOOM_MAX;
    };

    const show = (index) => {
        if (!gallery.length) return;
        mediaIndex = (index + gallery.length) % gallery.length;
        if (image) image.src = gallery[mediaIndex].image;
        setZoom(1);
        qsa('[data-media-thumb]', scope).forEach((thumb) => {
            thumb.classList.toggle('is-active', Number(thumb.dataset.mediaThumb) === mediaIndex);
        });
    };

    qs('[data-media-prev]', scope)?.addEventListener('click', () => show(mediaIndex - 1));
    qs('[data-media-next]', scope)?.addEventListener('click', () => show(mediaIndex + 1));
    qs('[data-media-zoom-in]', scope)?.addEventListener('click', () => setZoom(mediaZoom + 0.5));
    qs('[data-media-zoom-out]', scope)?.addEventListener('click', () => setZoom(mediaZoom - 0.5));

    const openFull = () => openLightbox(
        gallery.map((g) => ({src: g.image, thumb: g.image, caption: g.caption || ''})),
        mediaIndex
    );

    qs('[data-media-full]', scope)?.addEventListener('click', openFull);
    stage?.addEventListener('click', (event) => {
        if (event.target.closest('button')) return;
        openFull();
    });

    qsa('[data-media-thumb]', scope).forEach((thumb) => {
        thumb.addEventListener('click', () => show(Number(thumb.dataset.mediaThumb)));
    });

    qs('[data-open-quote]', scope)?.addEventListener('click', (event) => {
        const btn = event.currentTarget;
        openQuoteModal(btn.dataset.productId, btn.dataset.productTitle);
    });
}
